import type { Technique, TechniqueType } from '../types';
import { TYPE_ORDER } from '../constants/layout';

/** A technique that more than one clip covers — the same move, filmed by
 *  different creators (or by one creator twice, gi and no-gi). */
export interface TechniqueGroup {
  key: string;
  /** The shared name, taken from the lead so casing stays as it was typed. */
  name: string;
  /** The variant the collapsed card stands in for: its thumbnail, its tier
   *  colour, its type icon. */
  lead: Technique;
  /** Every clip in the group, lead first. A group of one is the normal case. */
  variants: Technique[];
}

/** Same move is the whole test: one name, one stack. Position and kind of move
 *  used to be part of the key, which split a move's escapes and transitions
 *  onto cards of their own; they are sections inside the chooser now (see
 *  `variantSections`) instead of separate stacks. Name is compared loosely
 *  (case and punctuation dropped) so "De La Riva" and "de-la-riva" land
 *  together. */
function groupKey(t: Technique): string {
  return t.name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Collapse a list of clips into display groups.
 *
 * This runs on the *filtered* list, never on the whole catalog, and that is the
 * whole trick: filter to one creator and every group is a single clip, so the
 * grouped card never appears where the user has already narrowed to one
 * version. Same for the gi/no-gi and belt filters, and for Favorites, where
 * only the clips the user actually starred are in the list.
 *
 * Order is preserved — a group sits where its first member sat. The callers
 * sort by type then name, so a stack lands where its first kind of move did:
 * a move with submissions and escapes sits with the submissions.
 */
export function groupTechniques(techs: Technique[]): TechniqueGroup[] {
  const byKey = new Map<string, Technique[]>();
  const order: string[] = [];
  for (const t of techs) {
    const key = groupKey(t);
    const list = byKey.get(key);
    if (list) list.push(t);
    else { byKey.set(key, [t]); order.push(key); }
  }
  return order.map((key) => {
    const variants = byKey.get(key)!;
    // Deterministic, so the collapsed card doesn't swap thumbnails between
    // renders — and in the order the chooser reads them out: kind of move
    // first (its sections), then easiest level, then creator, then id.
    if (variants.length > 1) {
      variants.sort((a, b) =>
        (TYPE_ORDER[a.type] - TYPE_ORDER[b.type])
        || (a.tier - b.tier)
        || (a.author ?? '').localeCompare(b.author ?? '')
        || a.id.localeCompare(b.id));
    }
    return { key, name: variants[0].name, lead: variants[0], variants };
  });
}

/** A stack's clips cut into runs of one kind of move, in `variants` order — so
 *  the chooser can head each run with "Escape", "Transition" and so on. A
 *  stack of one kind comes back as a single section, which the chooser leaves
 *  unheaded. */
export function variantSections(variants: Technique[]): { type: TechniqueType; items: Technique[] }[] {
  const out: { type: TechniqueType; items: Technique[] }[] = [];
  for (const t of variants) {
    const last = out[out.length - 1];
    if (last && last.type === t.type) last.items.push(t);
    else out.push({ type: t.type, items: [t] });
  }
  return out;
}
